import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Avatar,
  Divider,
  Paper,
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { makeSupportRequestForVersion } from "../../../Services/VersionsApi";

const MAX_MESSAGE_LENGTH = 1000;

const RequestPlaneDialog = ({ open, onClose, planeId }) => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const resetState = () => {
    setMessage("");
    setLoading(false);
    setError("");
    setSuccess("");
  };

  const handleClose = () => {
    if (loading) return; // منع الإغلاق أثناء الإرسال
    resetState();
    onClose && onClose();
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    if (!planeId) {
      setError("معرف الباقة مفقود.");
      return;
    }
    if (!message.trim()) {
      setError("الرسالة مطلوبة.");
      return;
    }
    if (message.length > MAX_MESSAGE_LENGTH) {
      setError(`الرسالة طويلة جداً، الحد الأقصى ${MAX_MESSAGE_LENGTH} حرفًا.`);
      return;
    }

    const payload = {
      plane_id: planeId,
      type: "request_plane",
      message: message.trim(),
    };

    try {
      setLoading(true);
      const res = await makeSupportRequestForVersion(payload);
      // تعامل مرن مع الشكل المتوقع للاستجابة
      const ok = res && (res.status === 200 || res.success);
      if (ok) {
        setSuccess("تم إرسال الطلب بنجاح. سيقوم فريق الدعم بالتواصل معك.");
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        setError((res && (res.data?.message || res.message)) || "حدث خطأ أثناء الإرسال.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "تعذر الاتصال بالخادم.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: 2, overflow: "visible", p: 0, boxShadow: 6 },
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 3, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: "linear-gradient(90deg,#1800ad,#f57226)",
              background:
                "linear-gradient(90deg, rgba(24,0,173,1) 0%, rgba(245,114,38,1) 100%)",
              width: 56,
              height: 56,
              boxShadow: "0 8px 24px rgba(24,0,173,0.12)",
            }}
          >
            <RocketLaunchIcon sx={{ color: "white" }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              طلب نسخة تجريبية
            </Typography>
            <Typography variant="body2" color="text.secondary">
              أرسل طلبك لفريق الدعم للحصول على نسخة من الباقة
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "flex", gap: 3, px: 3, py: 2 }}>
        <Box sx={{ flex: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            label="نص الطلب / رسالة للدعم"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            rows={5}
            fullWidth
            variant="outlined"
            placeholder="مثال: أود الحصول على نسخة تجريبية لمدة 7 أيام لباقة X، رقمي: 01XXXXXXXX"
            inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              الرجاء توضيح اسم المطعم ومدة النسخة المطلوبة إن أمكن
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {message.length}/{MAX_MESSAGE_LENGTH}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          إلغاء
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !message.trim()}
          startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <RocketLaunchIcon />}
          sx={{
            borderRadius: "28px",
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: 700,
            background: "linear-gradient(90deg, #1800ad 0%, #f57226 100%)",
            boxShadow: loading ? "none" : "0 8px 30px rgba(24,0,173,0.12)",
            "&:hover": { transform: "translateY(-2px)" },
          }}
        >
          {loading ? "جارٍ الإرسال..." : "إرسال الطلب"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestPlaneDialog;
